import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ExamList from './pages/ExamList';
import TakeExam from './pages/TakeExam';
import Results from './pages/Results';
import Instructions from './pages/Instructions';
import AboutUs from './pages/AboutUs';
import Policies from './pages/Policies';
import AboutSelectiveTests from './pages/AboutSelectiveTests';
import AboutOCTests from './pages/AboutOCTests';
import Testimonials from './pages/Testimonials';
import Pricing from './pages/Pricing';
import TopicTests from './pages/TopicTests';
import ElitePractice from './pages/ElitePractice';
import Register from './pages/Register';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';
import WritingStudio from './pages/WritingStudio';
import Analytics from './pages/Analytics';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import ResendVerification from './pages/ResendVerification';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showTestsDropdown, setShowTestsDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showHeader, setShowHeader] = useState(true);

  // Determine if current page should be full-width (no container)
  const isFullWidth = location.pathname.startsWith('/results') || 
                      location.pathname.startsWith('/dashboard') || 
                      location.pathname.startsWith('/exam/');
  const isMobileOrTablet = windowWidth <= 1024;
  const isDashboard = location.pathname === '/dashboard';
  const isExamPage = location.pathname.startsWith('/exam/');

  // Copy protection - disable right-click, copy, cut, and print
  useEffect(() => {
    const disableRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    const disableCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const disableCut = (e) => {
      e.preventDefault();
      return false;
    };

    const disableKeyboardShortcuts = (e) => {
      // Disable Ctrl+C, Ctrl+X, Ctrl+U (view source), Ctrl+S (save), Ctrl+P (print)
      if (e.ctrlKey && (e.key === 'c' || e.key === 'x' || e.key === 'u' || e.key === 's' || e.key === 'p')) {
        e.preventDefault();
        return false;
      }
      // TEMPORARILY DISABLED FOR DEBUGGING - Re-enable after troubleshooting
      // // Disable F12 (developer tools)
      // if (e.key === 'F12') {
      //   e.preventDefault();
      //   return false;
      // }
      // // Disable Ctrl+Shift+I (developer tools)
      // if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      //   e.preventDefault();
      //   return false;
      // }
    };

    // Add event listeners
    document.addEventListener('contextmenu', disableRightClick);
    document.addEventListener('copy', disableCopy);
    document.addEventListener('cut', disableCut);
    document.addEventListener('keydown', disableKeyboardShortcuts);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', disableRightClick);
      document.removeEventListener('copy', disableCopy);
      document.removeEventListener('cut', disableCut);
      document.removeEventListener('keydown', disableKeyboardShortcuts);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track scroll position for floating button and hide/show header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show button when scrolled down more than 200px
      setShowScrollToTop(currentScrollY > 200);
      
      // Hide header when scrolling down, show when scrolling up (only on mobile/tablet)
      if (isMobileOrTablet) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scrolling down
          setShowHeader(false);
        } else {
          // Scrolling up
          setShowHeader(true);
        }
      } else {
        setShowHeader(true); // Always show on desktop
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobileOrTablet]);

  useEffect(() => {
    // Check if user is logged in (from localStorage or Cognito)
    const checkAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          // If examType is missing from stored data, refresh from Cognito
          if (!parsed.examType) {
            try {
              const { getCurrentUser, fetchUserAttributes } = await import('aws-amplify/auth');
              const cognitoUser = await getCurrentUser();
              if (cognitoUser) {
                const attributes = await fetchUserAttributes();
                parsed.examType = attributes['custom:exam_type'] || 'selective';
                localStorage.setItem('user', JSON.stringify(parsed));
              }
            } catch (e) {
              // If Cognito fetch fails, default to selective
              parsed.examType = parsed.examType || 'selective';
            }
          }
          setUser(parsed);
        } else {
          // Try to get current Cognito user
          const { getCurrentUser, fetchUserAttributes } = await import('aws-amplify/auth');
          const cognitoUser = await getCurrentUser();
          if (cognitoUser) {
            const attributes = await fetchUserAttributes();
            const userData = {
              email: attributes.email,
              name: attributes.name || attributes.family_name,
              role: attributes.email === 'admin@prepsmart.com.au' ? 'admin' : 'student',
              examType: attributes['custom:exam_type'] || 'selective'
            };
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          }
        }
      } catch (err) {
        // User not logged in
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/';
    }
  };

  return (
    <div>
      <ScrollToTop />
      {!isExamPage && (
      <div className="header" style={{
        transform: showHeader ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out'
      }}>
        <div className="container">
          <div className="nav">
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', marginRight: '10px' }}>
              <img src="/logo.png" alt="PrepSmart" style={{ height: windowWidth <= 768 ? '80px' : (windowWidth <= 1024 ? '120px' : '180px'), width: 'auto', backgroundColor: 'transparent' }} />
            </Link>
            
            {/* Hamburger Menu Button (Mobile/Tablet Only - Hidden when logged in since we have bottom nav) */}
            {!user && (
              <button 
                className="mobile-menu-btn"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                style={{
                  display: 'none',
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                {showMobileMenu ? '✕' : '☰'}
              </button>
            )}

            {/* Desktop Navigation */}
            <div className="desktop-nav" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <button className="btn-secondary" style={{ padding: '8px 16px' }}>
                  🏠 Home
                </button>
              </Link>
              
              <Link to="/pricing" style={{ textDecoration: 'none' }}>
                <button className="btn-secondary" style={{ padding: '8px 16px' }}>
                  💰 Pricing
                </button>
              </Link>
              
              {/* About Dropdown */}
              <div style={{ position: 'relative', zIndex: 10001 }}>
                <button 
                  className="btn-secondary" 
                  style={{ padding: '8px 16px' }}
                  onClick={() => setShowAboutDropdown(!showAboutDropdown)}
                  onBlur={() => setTimeout(() => setShowAboutDropdown(false), 200)}
                >
                  About ▼
                </button>
                {showAboutDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '5px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    minWidth: '220px',
                    zIndex: 10002
                  }}>
                    <Link to="/about" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '12px 16px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        👥 About Us
                      </div>
                    </Link>
                    <Link to="/about-selective-tests" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '12px 16px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        📚 About Selective Tests
                      </div>
                    </Link>
                    <Link to="/about-oc-tests" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '12px 16px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        📖 About OC Tests
                      </div>
                    </Link>
                    <Link to="/testimonials" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '12px 16px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        💬 Testimonials
                      </div>
                    </Link>
                    <Link to="/policies" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '12px 16px',
                        color: '#333',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        📋 Our Policies
                      </div>
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Resources Dropdown with proper z-index */}
              <div style={{ position: 'relative', zIndex: 10001 }}>
                <button 
                  className="btn-secondary" 
                  style={{ padding: '8px 16px' }}
                  onClick={() => setShowTestsDropdown(!showTestsDropdown)}
                  onBlur={() => setTimeout(() => setShowTestsDropdown(false), 200)}
                >
                  Resources ▼
                </button>
                {showTestsDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '5px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                    minWidth: '240px',
                    zIndex: 10002,
                    overflow: 'hidden'
                  }}>
                    <Link to="/exams" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '14px 18px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.paddingLeft = '22px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.paddingLeft = '18px'; }}
                      >
                        <span style={{ fontSize: '18px' }}>🆓</span>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>Mock Tests</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Free practice exams</div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/topic-tests" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '14px 18px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.paddingLeft = '22px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.paddingLeft = '18px'; }}
                      >
                        <span style={{ fontSize: '18px' }}>📚</span>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>Topic-Based Tests</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Practice by subject area</div>
                        </div>
                      </div>
                    </Link>
                    <Link to="/elite-practice" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '14px 18px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#faf5ff'; e.currentTarget.style.paddingLeft = '22px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.paddingLeft = '18px'; }}
                      >
                        <span style={{ fontSize: '18px' }}>💎</span>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>Elite Practice</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Premium test bundle</div>
                        </div>
                      </div>
                    </Link>
                    {(!user || user.examType !== 'oc') && (
                    <Link to="/writing-studio" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '14px 18px',
                        color: '#333',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.paddingLeft = '22px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.paddingLeft = '18px'; }}
                      >
                        <span style={{ fontSize: '18px' }}>✍️</span>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>Writing Studio</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Selective only</div>
                        </div>
                      </div>
                    </Link>
                    )}
                    <Link to="/analytics" style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '14px 18px',
                        color: '#333',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f9ff'; e.currentTarget.style.paddingLeft = '22px'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.paddingLeft = '18px'; }}
                      >
                        <span style={{ fontSize: '18px' }}>📈</span>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>Analytics</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>Track your progress</div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              {user ? (
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  <Link to="/dashboard" style={{ textDecoration: 'none' }}>
                    <button className="btn-secondary" style={{ padding: '8px 16px' }}>
                      📊 Dashboard
                    </button>
                  </Link>
                  
                  {/* User Dropdown */}
                  <div style={{ position: 'relative', zIndex: 10001 }}>
                    <button 
                      className="btn-secondary" 
                      style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                    >
                      <span>👤</span>
                      <span>{user.name}</span>
                      <span style={{ fontSize: '12px' }}>▼</span>
                    </button>
                    {showUserDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '5px',
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                        minWidth: '180px',
                        zIndex: 10002
                      }}>
                        {user.role === 'admin' && (
                          <Link to="/admin" style={{ textDecoration: 'none' }}>
                            <div style={{
                              padding: '12px 16px',
                              color: '#333',
                              cursor: 'pointer',
                              borderBottom: '1px solid #e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <span>👨‍💼</span>
                              <span>Admin Panel</span>
                            </div>
                          </Link>
                        )}
                        <Link to="/change-password" style={{ textDecoration: 'none' }}>
                          <div style={{
                            padding: '12px 16px',
                            color: '#333',
                            cursor: 'pointer',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <span>🔒</span>
                            <span>Change Password</span>
                          </div>
                        </Link>
                        <div 
                          style={{
                            padding: '12px 16px',
                            color: '#dc2626',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontWeight: '600'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          onClick={handleLogout}
                        >
                          <span>🚪</span>
                          <span>Logout</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Link to="/login" style={{ textDecoration: 'none' }}>
                    <button className="btn-secondary" style={{ padding: '8px 16px' }}>
                      Login
                    </button>
                  </Link>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <button className="btn-primary" style={{ padding: '8px 16px' }}>
                      Register
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Mobile Menu Slide-in Drawer - Positioned outside header for proper full-screen overlay */}
      {showMobileMenu && !isExamPage && (
        <>
          {/* Backdrop/Overlay */}
          <div 
            onClick={() => setShowMobileMenu(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
              animation: 'fadeIn 0.3s ease'
            }}
          />
          
          {/* Slide-in Menu - Auto height, rounded corners, positioned above bottom nav */}
          <div style={{
            position: 'fixed',
            top: 'auto',
            right: 0,
            bottom: isMobileOrTablet && user ? '65px' : '0', // Above bottom nav if logged in
            width: '280px',
            maxHeight: isMobileOrTablet && user ? 'calc(100vh - 85px)' : '90vh', // Leave space for bottom nav
            background: 'white',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
            borderTopLeftRadius: '20px',
            borderBottomLeftRadius: isMobileOrTablet && user ? '0' : '20px',
            zIndex: 10000,
            overflowY: 'auto',
            animation: 'slideInRight 0.3s ease',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Menu Header */}
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderTopLeftRadius: '20px',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>More</h3>
                <p style={{ fontSize: '12px', opacity: '0.9' }}>Additional Options</p>
              </div>
              <button
                onClick={() => setShowMobileMenu(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                ✕
              </button>
            </div>

            {/* Menu Items */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Link to="/pricing" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>💰</span> <span>Pricing</span>
                </div>
              </Link>
              <Link to="/about" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>👥</span> <span>About Us</span>
                </div>
              </Link>
              <Link to="/about-selective-tests" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>📚</span> <span>About Selective Tests</span>
                </div>
              </Link>
              <Link to="/about-oc-tests" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>🎓</span> <span>About OC Tests</span>
                </div>
              </Link>
              <Link to="/testimonials" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>💬</span> <span>Testimonials</span>
                </div>
              </Link>
              <Link to="/policies" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                <div style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid #e5e7eb', 
                  color: '#333', 
                  fontSize: '15px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span>📋</span> <span>Our Policies</span>
                </div>
              </Link>
              
              {user && (
                <>
                  <div style={{ padding: '12px 20px', background: '#f9fafb', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Account
                  </div>
                  
                  <Link to="/change-password" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                    <div style={{ 
                      padding: '16px 20px', 
                      borderBottom: '1px solid #e5e7eb', 
                      color: '#333', 
                      fontSize: '15px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <span>🔒</span> <span>Change Password</span>
                    </div>
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                      <div style={{ 
                        padding: '16px 20px', 
                        borderBottom: '1px solid #e5e7eb', 
                        color: '#333', 
                        fontSize: '15px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <span>👨‍💼</span> <span>Admin Panel</span>
                      </div>
                    </Link>
                  )}
                  <div 
                    style={{ 
                      padding: '16px 20px', 
                      color: '#dc2626', 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      transition: 'background 0.2s',
                      borderBottomLeftRadius: isMobileOrTablet && user ? '0' : '20px'
                    }}
                    onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <span>🚪</span> <span>Logout</span>
                  </div>
                </>
              )}
              {!user && (
                <>
                  <div style={{ padding: '12px 20px', background: '#f9fafb', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Get Started
                  </div>
                  
                  <Link to="/login" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                    <div style={{ 
                      padding: '16px 20px', 
                      borderBottom: '1px solid #e5e7eb', 
                      color: '#333', 
                      fontSize: '15px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <span>🔑</span> <span>Login</span>
                    </div>
                  </Link>
                  <Link to="/register" style={{ textDecoration: 'none' }} onClick={() => setShowMobileMenu(false)}>
                    <div style={{ 
                      padding: '16px 20px', 
                      color: '#4f46e5', 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      transition: 'background 0.2s',
                      borderBottomLeftRadius: '20px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <span>✨</span> <span>Register</span>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <div className={isFullWidth ? '' : 'container'}>
        {authLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <p style={{ fontSize: '18px', color: '#666' }}>Loading...</p>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/instructions" element={<Instructions />} />
            <Route path="/about-selective-tests" element={<AboutSelectiveTests />} />
            <Route path="/about-oc-tests" element={<AboutOCTests />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/topic-tests" element={user ? <TopicTests /> : <Navigate to="/login" />} />
            <Route path="/elite-practice" element={user ? <ElitePractice /> : <Navigate to="/login" />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/change-password" element={user ? <ChangePassword /> : <Navigate to="/login" />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/writing-studio" element={user ? <WritingStudio /> : <Navigate to="/login" />} />
            <Route path="/analytics" element={user ? <Analytics /> : <Navigate to="/login" />} />
            <Route path="/exams" element={user ? <ExamList /> : <Navigate to="/login" />} />
            <Route path="/exam/:id" element={user ? <TakeExam /> : <Navigate to="/login" />} />
            <Route path="/results/:id" element={user ? <Results /> : <Navigate to="/login" />} />
            <Route path="/payment/success" element={user ? <PaymentSuccess /> : <Navigate to="/login" />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Routes>
        )}
      </div>

      {/* Bottom Navigation Bar - Only on Mobile/Tablet */}
      {isMobileOrTablet && user && !isExamPage && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '65px',
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom)' // For iPhone notch
        }}>
          <Link to="/" style={{ textDecoration: 'none', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>🏠</div>
            <div style={{ fontSize: '11px', color: location.pathname === '/' ? '#667eea' : '#6b7280', fontWeight: location.pathname === '/' ? '600' : '400' }}>Home</div>
          </Link>
          
          <Link to="/exams" style={{ textDecoration: 'none', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📝</div>
            <div style={{ fontSize: '11px', color: location.pathname.startsWith('/exam') ? '#667eea' : '#6b7280', fontWeight: location.pathname.startsWith('/exam') ? '600' : '400' }}>Tests</div>
          </Link>
          
          <Link to="/dashboard" style={{ textDecoration: 'none', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📊</div>
            <div style={{ fontSize: '11px', color: location.pathname === '/dashboard' ? '#667eea' : '#6b7280', fontWeight: location.pathname === '/dashboard' ? '600' : '400' }}>Dashboard</div>
          </Link>
          
          <Link to="/analytics" style={{ textDecoration: 'none', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📈</div>
            <div style={{ fontSize: '11px', color: location.pathname === '/analytics' ? '#667eea' : '#6b7280', fontWeight: location.pathname === '/analytics' ? '600' : '400' }}>Analytics</div>
          </Link>
          
          <button 
            onClick={() => setShowMobileMenu(true)}
            style={{ 
              background: 'none', 
              border: 'none', 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              padding: '8px 0',
              cursor: 'pointer'
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>☰</div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400' }}>More</div>
          </button>
        </div>
      )}

      {/* Add padding to content when bottom nav is visible */}
      {isMobileOrTablet && user && (
        <div style={{ height: '65px' }} />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
